
import { Injectable, OnDestroy } from '@angular/core';

import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { tap, switchMap } from 'rxjs/operators';
import { MessageService } from 'primeng/api';

import {
    MonitoringAccountConfigurationApi,
    ProminenceRecalculationJobStatus
} from '../../../api/monitoring/monitoring-account-configuration.api';

import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { gateCall } from '../../../Core/rxjs-utils';

import { messageSeverityEnum } from '../enums/message-severity.enum';
import { WebsocketAwareNeptuneService } from '../../../Bootstrap/services/websocket-aware-neptune.service';

@Injectable()
export class ProminenceLockerService implements OnDestroy {

    prominenceStatus$: BehaviorSubject<ProminenceRecalculationJobStatus> = new BehaviorSubject<ProminenceRecalculationJobStatus>(null);
    cachedStatus: ProminenceRecalculationJobStatus;

    readonly messageSeverityEnum = messageSeverityEnum;
    subscriptions: Subscription[] = [];

    constructor(
        private monitoringConfigurationApi: MonitoringAccountConfigurationApi,
        private messageService: MessageService,
        private localizationService: LocalizationService,
        private currentUserStateService: CurrentUserStateService,
        private websocketService: WebsocketAwareNeptuneService,
    ) {

        // check to see if the user has adv mon permissions before making the api call
        gateCall(this.currentUserStateService.hasAdvancedMonitoringAccess()).pipe(
            switchMap(() => this.getProminenceCalculationStatus())
        ).subscribe();


        this.subscriptions.push(
            this.websocketService.getWebsocketMessages().subscribe(message => {
                if (message?.resourceType === 'prominence') {
                    if (message.slug.indexOf('status=DONE') !== -1) {
                        this.prominenceStatus$.next(ProminenceRecalculationJobStatus.DONE);
                        this.checkForUpdates(ProminenceRecalculationJobStatus.DONE);
                    } else if (message.slug.indexOf('status=FAILED') !== -1) {
                        this.prominenceStatus$.next(ProminenceRecalculationJobStatus.FAILED);
                        this.checkForUpdates(ProminenceRecalculationJobStatus.FAILED);
                    }
                }

            }),
        );
    }

    public forceCheck() {
        this.getProminenceCalculationStatus().subscribe();
    }

    getProminenceCalculationStatus(): Observable<any> {
        return this.monitoringConfigurationApi.getProminenceCalculationStatus().pipe(
            tap(status => {
                this.prominenceStatus$.next(status);
                this.checkForUpdates(status);
            })
        );
    }

    private checkForUpdates(status: ProminenceRecalculationJobStatus) {
        if (!!this.cachedStatus
            && (this.cachedStatus === ProminenceRecalculationJobStatus.ONGOING
                || this.cachedStatus === ProminenceRecalculationJobStatus.PENDING)
            && (status === ProminenceRecalculationJobStatus.DONE
                || status === ProminenceRecalculationJobStatus.FAILED)) {
            const severity = status === ProminenceRecalculationJobStatus.DONE ? messageSeverityEnum.success : messageSeverityEnum.error;
            const messageSummary = `AccountPreferences.prominence.update.message.${severity}`;
            const messageDetail = `AccountPreferences.prominence.update.detail.${severity}`;
            this.messageService.add({
                severity: severity,
                summary: this.localizationService.transform(messageSummary),
                detail: this.localizationService.transform(messageDetail)
            });
        }
        this.cachedStatus = status;
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }
}
